import { normalize, schema } from 'normalizr';
import Immutable from 'seamless-immutable';

import * as api from '~/api';
import pipelinesApi from '~/api/pipelines';
import { ALL_PROJECTS_ID } from '~/common/helpers/constants';
import i18n from '~/common/helpers/i18n';

const normalizeData = ({ entity, data }) => {
  const Schema = new schema.Entity(entity);
  const mySchema = { [entity]: [Schema] };
  return normalize({ [entity]: data }, mySchema);
};

const removeById = (allIds, byId, id) => {
  const newFolder = allIds.filter(x => x !== id);
  const newById = newFolder.reduce(
    (acc, current) => ({
      ...acc,
      [current]: {
        ...byId[current]
      }
    }),
    {}
  );
  return {
    result: {
      folder: newFolder
    },
    entities: {
      folder: {
        ...newById
      }
    }
  };
};

const initialState = Immutable({
  loading: {
    save: false,
    fetch: false
  },
  result: {
    folder: []
  },
  entities: {
    folder: {}
  },
  error: {
    save: null,
    fetch: null,
    remove: null
  },
  save: {
    isOpen: false,
    id: null,
    entity: null,
    projectId: null
  }
});

const folderModel = {
  name: 'folder',
  state: initialState,
  reducers: {
    setLoading(state, { path, value }) {
      return state.setIn(['loading', path], value);
    },
    setError(state, { path, value }) {
      return state.setIn(['error', path], value);
    },
    setSuccess(state, source) {
      const newResult = source?.result?.folder || [];
      const newEntities = source?.entities?.folder || {};
      const shouldAccumulate = source?.accumulate !== false;

      if (shouldAccumulate) {
        const existingResult = state.result.folder || [];
        const existingEntities = state.entities.folder || {};

        const mergedResult = [...new Set([...existingResult, ...newResult])];
        const mergedEntities = {
          ...existingEntities,
          ...newEntities
        };

        return state.merge({
          result: {
            folder: mergedResult
          },
          entities: {
            folder: mergedEntities
          }
        });
      }

      return state.merge({
        result: {
          folder: newResult
        },
        entities: {
          folder: newEntities
        }
      });
    },
    setSave(state, { path, value }) {
      return state.setIn(['save', path], value);
    },
    setRemove(state, source) {
      return state.merge(source);
    },
    clear() {
      return initialState;
    }
  },
  effects: dispatch => ({
    async fetchAll(params, { application, authentication }) {
      try {
        const { realm } = application.realm;
        const { token } = authentication.userData;

        const { folders } = await api.folder.fetchAll({
          realm,
          token
        });

        const source = normalizeData({
          entity: 'folder',
          data: folders
        });

        dispatch.folder.setSuccess({ ...source, accumulate: false });
      } catch (e) {
        dispatch.folder.setError({ path: 'fetchAll', value: e });
        dispatch.snackbar.create({
          text: i18n.t('message.error.fetch'),
          action: { label: i18n.t('common.labels.ok') }
        });
      }
    },
    async fetch(params, { application, authentication, project }) {
      try {
        const { realm } = application.realm;
        const { token } = authentication.userData;
        const projectId = params?.projectId || project?.current;

        if (!projectId || projectId === ALL_PROJECTS_ID) {
          return;
        }

        const { folders } = await api.folder.fetchByProject({
          realm,
          token,
          projectId
        });

        const source = normalizeData({
          entity: 'folder',
          data: folders
        });

        dispatch.folder.setSuccess({ ...source, accumulate: true });
      } catch (e) {
        dispatch.folder.setError({ path: 'fetch', value: e });
        dispatch.snackbar.create({
          text: i18n.t('message.error.fetch'),
          action: { label: i18n.t('common.labels.ok') }
        });
      }
    },
    async save(params, { application, authentication }) {
      try {
        const { realm } = application.realm;
        const { token } = authentication.userData;
        const { folder: folderData } = await api.folder.save({
          realm,
          token,
          projectId: params.projectId,
          name: params.name
        });
        const folderSchema = new schema.Entity('folder');
        const source = normalize(folderData, folderSchema);
        dispatch.folder.setSuccess({ ...source, accumulate: true });
        dispatch.folder.setSave({
          path: 'isOpen',
          value: false
        });
        dispatch.snackbar.create({
          text: i18n.t('label.create_folder_success', {
            folderName: params.name
          }),
          action: { label: i18n.t('common.labels.ok') }
        });
        dispatch.folder.fetch({ projectId: params.projectId });
        dispatch.project.fetch();
      } catch (e) {
        dispatch.folder.setError({ path: 'save', value: e });
        dispatch.folder.setLoading({ path: 'save', value: false });
        dispatch.snackbar.create({
          text: i18n.t('label.create_folder_error_message'),
          action: { label: i18n.t('common.labels.ok') }
        });
      }
    },
    async edit(params, { application, authentication }) {
      try {
        const { realm } = application.realm;
        const { token } = authentication.userData;
        const { folder: folderData } = await api.folder.edit({
          realm,
          token,
          projectId: params.projectId,
          folderId: params.folderId,
          name: params.name
        });
        const folderSchema = new schema.Entity('folder');
        const source = normalize(folderData, folderSchema);
        dispatch.folder.setSuccess({ ...source, accumulate: true });
        dispatch.folder.setSave({
          path: 'isOpen',
          value: false
        });
        dispatch.snackbar.create({
          text: i18n.t('label.update_folder_success', {
            folderName: params.name
          }),
          action: { label: i18n.t('common.labels.ok') }
        });
        dispatch.folder.fetch({ projectId: params.projectId });
      } catch (e) {
        dispatch.folder.setError({ path: 'save', value: e });
        dispatch.folder.setLoading({ path: 'save', value: false });
        dispatch.snackbar.create({
          text: i18n.t('label.update_folder_error_message'),
          action: { label: i18n.t('common.labels.ok') }
        });
      }
    },
    async remove(params, { application, authentication, folder }) {
      try {
        const { realm } = application.realm;
        const { token } = authentication.userData;
        const currentFolder = folder.entities.folder[params.folderId];

        const { data } = await pipelinesApi.fetch({
          realm,
          search: {
            projectId: params.projectId,
            folderId: params.folderId,
            page: 0
          }
        });

        const pipelinesInFolder = data?.searchPipelines?.content || [];

        if (pipelinesInFolder.length > 0) {
          dispatch.snackbar.create({
            text: i18n.t('label.cannot_archive_folder_with_pipelines'),
            action: { label: i18n.t('common.labels.ok') }
          });
          return;
        }

        await api.folder.remove({
          realm,
          token,
          projectId: params.projectId,
          folderId: params.folderId
        });

        const source = removeById(
          folder.result.folder,
          folder.entities.folder,
          params.folderId
        );

        dispatch.folder.setRemove(source);
        dispatch.snackbar.create({
          text: i18n.t('label.archive_folder_success', {
            folderName: currentFolder.name
          }),
          action: { label: i18n.t('common.labels.ok') }
        });

        dispatch.folder.fetch({ projectId: params.projectId });

        dispatch.project.fetch();
      } catch (e) {
        dispatch.folder.setError({ path: 'remove', value: e });
        dispatch.snackbar.create({
          text: i18n.t('label.archive_folder_error_message'),
          action: { label: i18n.t('common.labels.ok') }
        });
      }
    },
    async movePipelinesToFolder(
      params,
      { application, authentication, pipelines }
    ) {
      try {
        const { realm } = application.realm;
        const { token } = authentication.userData;
        const { pipelines: byIds } = pipelines.entities;

        await Promise.all(
          params.pipelineIds.map(pipelineId => {
            const pipeline = byIds[pipelineId];
            return api.folder.movePipelineToFolder({
              realm,
              token,
              projectId: params.projectId,
              pipelineName: pipeline?.name,
              pipelineVersionMajor: pipeline?.versionMajor,
              folderIdOrName: params.folderId || ''
            });
          })
        );

        dispatch.snackbar.create({
          text: i18n.t('label.move_pipeline_to_folder_success', {
            count: params.pipelineIds.length
          }),
          action: { label: i18n.t('common.labels.ok') }
        });
      } catch (e) {
        dispatch.snackbar.create({
          text: i18n.t('label.move_pipeline_to_folder_error'),
          action: { label: i18n.t('common.labels.ok') }
        });
      }
    }
  }),
  logics: [
    {
      type: ['folder/fetch'],
      latest: true,
      process(context, dispatch, done) {
        dispatch.folder.setLoading({ path: 'fetch', value: true });
        done();
      }
    },
    {
      type: ['folder/save'],
      latest: true,
      process(context, dispatch, done) {
        dispatch.folder.setLoading({ path: 'save', value: true });
        done();
      }
    },
    {
      type: ['folder/edit'],
      latest: true,
      process(context, dispatch, done) {
        dispatch.folder.setLoading({ path: 'save', value: true });
        done();
      }
    }
  ]
};

export default folderModel;
